import { VerlengingStatus, VestigingStatus } from "@/entities/vestiging/constants";
import { AangevraagdeVestiging, Vestiging } from "@/entities/vestiging/domain";
import { ActionsDropdown, Badge, Button, DropdownMenuItem, Icon, Tooltip, TooltipContent, TooltipTrigger } from "@/shared/components";
import { useState } from "react";
import { VestigingenUpdateHandler } from "../../shared";
import { ToelichtingDialog } from "../shared/toelichting-dialog";
import { BehandelAanvraagDialogFactory, BehandelAanvraagDialogType } from "./behandel-aanvraag-dialog";
import { GaatNietLeverenConfirmationDialog } from "./gaat-niet-leveren-confirmation-dialog";

interface ToelichtingColProps {
    vestiging: Vestiging;
}

export function ToelichtingCol({ vestiging }: Readonly<ToelichtingColProps>) {
    if (!vestiging.aangevraagdeVestiging!.toelichtingKlantenservice) {
        return null;
    }

    return (
        <Tooltip>
            <TooltipTrigger asChild>
                <Icon name="comment" alt="toelichting" className="text-content-emphasis" />
            </TooltipTrigger>
            <TooltipContent>
                <p>{vestiging.aangevraagdeVestiging!.toelichtingKlantenservice}</p>
            </TooltipContent>
        </Tooltip>
    );
}

interface MenuColProps {
    vestiging: Vestiging;
    variant: "startNsRetailVestiging" | "stopNsRetailVestiging";
    onVestigingUpdate: VestigingenUpdateHandler;
    onToelichtingChange: (vestiging: Vestiging, toelichting: string) => void;
}

export function MenuCol({ vestiging, variant, onVestigingUpdate, onToelichtingChange }: Readonly<MenuColProps>) {
    const [toelichtingDialogOpen, setToelichtingDialogOpen] = useState(false);
    const [gaatNietLeverenConfirmationDialogOpen, setGaatNietLeverenConfirmationDialogOpen] = useState(false);

    const handleToelichtingSubmit = (toelichting: string) => {
        onToelichtingChange(vestiging, toelichting);
        setToelichtingDialogOpen(false);
    };

    const updateVestiging = (update: Partial<AangevraagdeVestiging>) => {
        void onVestigingUpdate({
            [vestiging.aangevraagdeVestiging!.id]: AangevraagdeVestiging.update(vestiging.aangevraagdeVestiging!, update),
        });
        setGaatNietLeverenConfirmationDialogOpen(false);
    };

    return (
        <>
            <ActionsDropdown data-testid="vestiging-toelichting">
                <DropdownMenuItem onClick={() => setToelichtingDialogOpen(true)}>
                    {vestiging.aangevraagdeVestiging!.toelichtingKlantenservice ? "Toelichting wijzigen" : "Toelichting toevoegen"}
                </DropdownMenuItem>
                {variant === "startNsRetailVestiging" && (
                    <DropdownMenuItem key="gaat-niet-leveren" onClick={() => setGaatNietLeverenConfirmationDialogOpen(true)}>
                        Gaat niet leveren
                    </DropdownMenuItem>
                )}
            </ActionsDropdown>

            <ToelichtingDialog
                open={toelichtingDialogOpen}
                onOpenChange={setToelichtingDialogOpen}
                titelSuffix={Vestiging.getDisplayName(vestiging)}
                existingToelichting={vestiging.aangevraagdeVestiging?.toelichtingKlantenservice}
                onSave={handleToelichtingSubmit}
            />

            <GaatNietLeverenConfirmationDialog
                key={`${gaatNietLeverenConfirmationDialogOpen}`}
                open={gaatNietLeverenConfirmationDialogOpen}
                onOpenChange={setGaatNietLeverenConfirmationDialogOpen}
                initialToelichting={vestiging.aangevraagdeVestiging?.toelichtingKlantenservice ?? ""}
                onConfirm={(toelichting) =>
                    void updateVestiging({
                        status: "GaatNietLeveren",
                        toelichtingKlantenservice: toelichting,
                    })
                }
            />
        </>
    );
}

const getBadgeForVestiging = (vestiging: Vestiging): React.ReactElement | null => {
    if (
        vestiging.currentOpeningstijden.aangepasteOpenTotStatus === VerlengingStatus.AANGEVRAAGD &&
        vestiging.aangevraagdeVestiging?.status !== VestigingStatus.STOP_AANGEVRAAGD
    ) {
        if (vestiging.aangevraagdeVestiging?.status === VestigingStatus.BESTELD) {
            return (
                <Badge variant="green-subtle" className="block truncate text-xs">
                    Startverzoek met verlenging
                </Badge>
            );
        } else {
            return (
                <Badge variant="blue-subtle" className="block truncate text-xs">
                    Verzoek openingstijd:
                    <span className="font-bold"> {vestiging.currentOpeningstijden.aangepasteOpenTot}</span>
                </Badge>
            );
        }
    }

    switch (vestiging.aangevraagdeVestiging?.status) {
        case VestigingStatus.BESTELD:
            return (
                <Badge variant="green-subtle" className="text-xs">
                    Startverzoek
                </Badge>
            );
        case VestigingStatus.STOP_AANGEVRAAGD:
            return (
                <Badge variant="red-subtle" className="text-xs">
                    Stopverzoek
                </Badge>
            );
        default:
            return null;
    }
};

type LokaleVestigingenBadgeAndButtonProps = {
    vestiging: Vestiging;
    onVestigingUpdate: VestigingenUpdateHandler;
};

export function LokaleVestigingenBadgeAndButton({ vestiging, onVestigingUpdate }: Readonly<LokaleVestigingenBadgeAndButtonProps>) {
    const [behandelAanvraagDialogOpen, setBehandelAanvraagDialogOpen] = useState(false);

    const getDialogType = (): BehandelAanvraagDialogType => {
        if (
            vestiging.currentOpeningstijden.aangepasteOpenTotStatus === VerlengingStatus.AANGEVRAAGD &&
            vestiging.aangevraagdeVestiging?.status !== VestigingStatus.STOP_AANGEVRAAGD
        ) {
            return vestiging.aangevraagdeVestiging?.status === VestigingStatus.BESTELD ? "BesteldMetVerlenging" : "VerlengingAangevraagd";
        }
        return vestiging.aangevraagdeVestiging?.status as BehandelAanvraagDialogType;
    };

    return (
        <div className="flex items-center justify-end gap-4">
            {getBadgeForVestiging(vestiging)}
            <Button variant={"secondary"} size="s" className="min-w-20" onClick={() => setBehandelAanvraagDialogOpen(true)}>
                Behandelen
            </Button>
            <BehandelAanvraagDialogFactory
                open={behandelAanvraagDialogOpen}
                onOpenChange={setBehandelAanvraagDialogOpen}
                vestiging={vestiging}
                type={getDialogType()}
                onVestigingUpdate={onVestigingUpdate}
            />
        </div>
    );
}
